import {
  DEFAULT_WORLD_BALANCE,
  type WorldBalance,
} from "../../models/config/worldBalance.ts";
import type {
  TileCoordinates,
  TileData,
  TileKey,
  TileTerrain,
} from "../../models/worldState.ts";
import { tileKey } from "../../models/worldState.ts";

export function getFoodProduction(
  terrain: TileTerrain,
  balance: WorldBalance = DEFAULT_WORLD_BALANCE,
): number {
  return balance.terrainFoodProduction[terrain];
}

export function formatFoodProduction(
  terrain: TileTerrain,
  balance: WorldBalance = DEFAULT_WORLD_BALANCE,
): string {
  return String(getFoodProduction(terrain, balance));
}

export function getEffectiveFoodProduction(
  terrain: TileTerrain,
  foodDepletion: number,
  balance: WorldBalance = DEFAULT_WORLD_BALANCE,
): number {
  const base = getFoodProduction(terrain, balance);
  const minYield = base * balance.tileFoodMinYieldFraction;
  const clampedDepletion = Math.min(1, Math.max(0, foodDepletion));
  const yieldRange = base - minYield;
  return Math.round(minYield + yieldRange * (1 - clampedDepletion));
}

export function formatEffectiveFoodProduction(
  terrain: TileTerrain,
  foodDepletion: number,
  balance: WorldBalance = DEFAULT_WORLD_BALANCE,
): string {
  return String(getEffectiveFoodProduction(terrain, foodDepletion, balance));
}

function increaseFoodDepletion(
  foodDepletion: number,
  balance: WorldBalance = DEFAULT_WORLD_BALANCE,
): number {
  return Math.min(1, foodDepletion + balance.tileFoodDepletionStep);
}

function decreaseFoodDepletion(
  foodDepletion: number,
  balance: WorldBalance = DEFAULT_WORLD_BALANCE,
): number {
  return Math.max(0, foodDepletion - balance.tileFoodRegenStep);
}

export function applyEndTurnTileFood(
  tiles: Record<TileKey, TileData>,
  clanTile: TileCoordinates,
  stayedOnTile: boolean,
  balance: WorldBalance = DEFAULT_WORLD_BALANCE,
): { tiles: Record<TileKey, TileData>; foodProduction: number | null } {
  const clanKey = tileKey(clanTile.q, clanTile.r);
  const clanTileData = tiles[clanKey];
  if (!clanTileData) {
    return { tiles, foodProduction: null };
  }

  const effectiveProduction = stayedOnTile
    ? getEffectiveFoodProduction(
        clanTileData.terrain,
        clanTileData.foodDepletion,
        balance,
      )
    : 0;

  const nextTiles: Record<TileKey, TileData> = {};

  for (const [key, tile] of Object.entries(tiles)) {
    if (key === clanKey) {
      nextTiles[key as TileKey] = stayedOnTile
        ? {
            ...tile,
            foodDepletion: increaseFoodDepletion(tile.foodDepletion, balance),
          }
        : { ...tile };
      continue;
    }

    nextTiles[key as TileKey] = {
      ...tile,
      foodDepletion: decreaseFoodDepletion(tile.foodDepletion, balance),
    };
  }

  return { tiles: nextTiles, foodProduction: effectiveProduction };
}
